import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Check, X, AlertCircle, Settings, Link as LinkIcon, Scissors, Play, Trash2 } from 'lucide-react';
import { API_URL } from '../lib/apiConfig';

const safeToken = () => { try { return localStorage.getItem('autopostr_token'); } catch { return null; } };

const VIDEO_TYPES = [
  { value: 2, label: 'YouTube' },
  { value: 1, label: 'Remote file (MP4 URL)' },
  { value: 3, label: 'Google Drive' },
  { value: 4, label: 'Vimeo' },
  { value: 10, label: 'Loom' },
  { value: 11, label: 'Facebook' },
  { value: 12, label: 'LinkedIn' },
  { value: 13, label: 'Dropbox' },
  { value: 7, label: 'Twitter / X' },
  { value: 14, label: 'Instagram' },
  { value: 6, label: 'TikTok' },
];

const PREFER_LENGTH = [
  { value: 0, label: 'Auto' },
  { value: 1, label: '< 30s' },
  { value: 2, label: '30–60s' },
  { value: 3, label: '60–90s' },
  { value: 4, label: '90s–3m' },
];

const browserOffsetIso = () => {
  const m = -new Date().getTimezoneOffset();
  const sign = m >= 0 ? '+' : '-';
  const abs = Math.abs(m);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
};

export default function VizardScheduler({ accounts, isPro, onScheduled }) {
  const [settings, setSettings] = useState({ configured: false, masked: '' });
  const [showKeyEntry, setShowKeyEntry] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoType, setVideoType] = useState(2);
  const [preferLength, setPreferLength] = useState(0);
  const [maxClips, setMaxClips] = useState(10);
  const [projectId, setProjectId] = useState('');
  const [projectStatus, setProjectStatus] = useState(null); // null | processing | ready | error
  const [clips, setClips] = useState([]);
  const [selectedClips, setSelectedClips] = useState({}); // id -> true
  const [captions, setCaptions] = useState({}); // id -> caption text
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [slotMode, setSlotMode] = useState('slots'); // slots | interval
  const [timeSlots, setTimeSlots] = useState(['09:00', '13:00', '18:00']);
  const [newSlot, setNewSlot] = useState('');
  const [intervalHours, setIntervalHours] = useState(6);
  const [scheduling, setScheduling] = useState(false);
  const [message, setMessage] = useState(null);
  const [savedProjects, setSavedProjects] = useState([]);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const pollTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/vizard/settings`, { headers: { Authorization: `Bearer ${safeToken()}` } });
        if (r.ok) setSettings(await r.json());
      } catch {}
    })();
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, []);

  const saveKey = async () => {
    if (!apiKeyInput.trim()) return;
    setSavingKey(true);
    try {
      const r = await fetch(`${API_URL}/api/vizard/settings`, {
        method: 'POST', headers: { Authorization: `Bearer ${safeToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKeyInput.trim() })
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed');
      const s = await fetch(`${API_URL}/api/vizard/settings`, { headers: { Authorization: `Bearer ${safeToken()}` } });
      setSettings(await s.json());
      setApiKeyInput('');
      setShowKeyEntry(false);
      setMessage({ type: 'success', text: 'Vizard API key saved.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSavingKey(false);
    }
  };

  const removeKey = async () => {
    if (!window.confirm('Remove Vizard API key?')) return;
    const r = await fetch(`${API_URL}/api/vizard/settings`, { method: 'DELETE', headers: { Authorization: `Bearer ${safeToken()}` } });
    if (r.ok) {
      setSettings({ configured: false, masked: '' });
      setMessage({ type: 'success', text: 'Vizard API key removed.' });
    }
  };

  const pollProject = async (pid) => {
    try {
      const r = await fetch(`${API_URL}/api/vizard/projects/${pid}`, { headers: { Authorization: `Bearer ${safeToken()}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Vizard query failed');
      setProjectStatus(data.status);
      if (data.status === 'ready') {
        setClips(data.videos || []);
        const defaults = {};
        (data.videos || []).forEach(v => { defaults[v.videoId] = v.title || ''; });
        setCaptions(defaults);
        setPolling(false);
        return;
      }
      if (data.status === 'error') {
        setMessage({ type: 'error', text: data.message || 'Vizard returned an error' });
        setPolling(false);
        return;
      }
      // still processing — poll again in 20s
      pollTimer.current = setTimeout(() => pollProject(pid), 20000);
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
      setPolling(false);
    }
  };

  const createProject = async () => {
    if (!videoUrl.trim()) { setMessage({ type: 'error', text: 'Paste a video URL first.' }); return; }
    setSubmitting(true);
    setMessage(null);
    setClips([]);
    setSelectedClips({});
    setProjectStatus(null);
    setProjectId('');
    try {
      const r = await fetch(`${API_URL}/api/vizard/projects`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${safeToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoUrl: videoUrl.trim(),
          videoType: Number(videoType),
          preferLength: [Number(preferLength)],
          maxClipNumber: Number(maxClips),
          lang: 'en',
          ratioOfClip: 1,
        })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Vizard rejected the request');
      setProjectId(data.projectId);
      setProjectStatus('processing');
      setPolling(true);
      pollTimer.current = setTimeout(() => pollProject(data.projectId), 15000);
      setMessage({ type: 'success', text: `Vizard is generating clips (project ${data.projectId}). This usually takes a few minutes.` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const loadExisting = async () => {
    try {
      const r = await fetch(`${API_URL}/api/vizard/projects`, { headers: { Authorization: `Bearer ${safeToken()}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed to load projects');
      if (!(data.projects || []).length) {
        setMessage({ type: 'error', text: 'No saved projects yet. Generate your first batch of clips above.' });
        return;
      }
      setSavedProjects(data.projects);
      setShowProjectPicker(true);
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const pickProject = (pid) => {
    setShowProjectPicker(false);
    setProjectId(pid);
    setClips([]);
    setSelectedClips({});
    setProjectStatus('processing');
    setPolling(true);
    pollProject(pid);
  };

  const toggleAccount = (id) => setSelectedAccounts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleClip = (id) => setSelectedClips(prev => ({ ...prev, [id]: !prev[id] }));

  const addSlot = () => {
    if (!/^\d{2}:\d{2}$/.test(newSlot)) return;
    if (!timeSlots.includes(newSlot)) setTimeSlots(prev => [...prev, newSlot].sort());
    setNewSlot('');
  };

  const schedule = async () => {
    const chosen = clips.filter(c => selectedClips[c.videoId]);
    if (!chosen.length) { setMessage({ type: 'error', text: 'Select at least one clip.' }); return; }
    if (!selectedAccounts.length) { setMessage({ type: 'error', text: 'Select at least one account.' }); return; }
    setScheduling(true);
    setMessage(null);
    try {
      const body = {
        items: chosen.map(c => ({ media_url: c.videoUrl, caption: captions[c.videoId] || '' })),
        account_ids: selectedAccounts,
        mode: slotMode,
        start_date: startDate,
        time_slots: timeSlots,
        interval_hours: Number(intervalHours),
        tz_offset: browserOffsetIso(),
      };
      const r = await fetch(`${API_URL}/api/vizard/bulk-schedule`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${safeToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Bulk schedule failed');
      setMessage({
        type: data.failed_count ? 'error' : 'success',
        text: `${data.scheduled_count} clip(s) scheduled${data.failed_count ? `, ${data.failed_count} failed` : ''}.`
      });
      if (data.scheduled_count && onScheduled) onScheduled();
      setSelectedClips({});
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setScheduling(false);
    }
  };

  if (!isPro) {
    return (
      <div data-testid="vizard-upgrade" className="p-10 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/30 text-center">
        <Scissors size={40} className="mx-auto text-emerald-400 mb-3" />
        <h2 className="text-2xl font-bold text-white mb-2">Bulk schedule with Vizard</h2>
        <p className="text-white/60 mb-5">Paste a YouTube link, let Vizard cut your video into viral clips, then schedule them across every connected account.</p>
        <a href="/pricing" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600">Upgrade to Pro</a>
      </div>
    );
  }

  const chosenCount = clips.filter(c => selectedClips[c.videoId]).length;

  return (
    <div className="space-y-6" data-testid="vizard-scheduler">
      {showProjectPicker && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="vizard-picker-modal">
          <div className="bg-[#111] border border-white/10 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-semibold">Load Vizard project</h3>
              <button onClick={() => setShowProjectPicker(false)} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto p-2">
              {savedProjects.map(p => (
                <button key={p.project_id} data-testid={`vizard-picker-${p.project_id}`} onClick={() => pickProject(p.project_id)}
                  className="w-full text-left p-3 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10">
                  <p className="text-white text-sm font-medium truncate">{p.project_name || `Project ${p.project_id}`}</p>
                  <p className="text-white/40 text-xs truncate">{p.video_url}</p>
                  <p className="text-white/30 text-[11px] mt-0.5">{new Date(p.created_at).toLocaleString()}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* API key banner */}
      <div className="p-4 rounded-xl bg-[#111] border border-white/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center"><Settings size={18} className="text-emerald-400" /></div>
          <div>
            <p className="text-white font-medium text-sm">Vizard API key</p>
            <p className="text-white/50 text-xs">{settings.configured ? `Saved — ${settings.masked}` : 'Add your Vizard key to generate clips.'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="vizard-edit-key" onClick={() => setShowKeyEntry(v => !v)} className="px-3 py-1.5 rounded-lg bg-white/5 text-white/80 text-xs hover:bg-white/10">{settings.configured ? 'Update' : 'Add key'}</button>
          {settings.configured && <button onClick={removeKey} className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20">Remove</button>}
        </div>
      </div>
      {showKeyEntry && (
        <div className="p-4 rounded-xl bg-[#0f0f0f] border border-emerald-500/30">
          <label className="block text-white/70 text-xs mb-2">Vizard API key (from Vizard → Workspace Settings → API)</label>
          <div className="flex gap-2">
            <input data-testid="vizard-key-input" value={apiKeyInput} onChange={e => setApiKeyInput(e.target.value)} type="password"
              placeholder="VIZARDAI_API_KEY" className="flex-1 p-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
            <button data-testid="vizard-save-key" disabled={savingKey || !apiKeyInput.trim()} onClick={saveKey}
              className="px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium disabled:opacity-50">
              {savingKey ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
            </button>
          </div>
          <p className="text-white/40 text-[11px] mt-2">Stored securely on your AutoPostr account. Never shared with other users.</p>
        </div>
      )}

      {message && (
        <div data-testid="vizard-message" className={`p-3 rounded-xl flex items-center gap-2 text-sm ${message.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border border-red-500/30 text-red-400'}`}>
          {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />} {message.text}
        </div>
      )}

      {/* Source video */}
      <div className="p-5 rounded-2xl bg-[#111] border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <LinkIcon size={16} className="text-emerald-400" />
          <h3 className="text-white font-semibold">Source video</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_160px_120px] gap-3">
          <input data-testid="vizard-video-url" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="Paste YouTube / Vimeo / MP4 URL"
            className="p-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30" />
          <select data-testid="vizard-video-type" value={videoType} onChange={e => setVideoType(e.target.value)} className="p-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm">
            {VIDEO_TYPES.map(v => <option key={v.value} value={v.value} className="bg-[#1a1a1a]">{v.label}</option>)}
          </select>
          <select data-testid="vizard-prefer-length" value={preferLength} onChange={e => setPreferLength(e.target.value)} className="p-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm">
            {PREFER_LENGTH.map(v => <option key={v.value} value={v.value} className="bg-[#1a1a1a]">{v.label}</option>)}
          </select>
          <input data-testid="vizard-max-clips" type="number" min="1" max="20" value={maxClips} onChange={e => setMaxClips(e.target.value)}
            className="p-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm" title="Max clips" />
        </div>
        <div className="flex gap-3 mt-4">
          <button data-testid="vizard-create-project" disabled={submitting || !settings.configured} onClick={createProject}
            className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold disabled:opacity-50 flex items-center gap-2 text-sm">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Scissors size={16} />} Generate clips
          </button>
          <button data-testid="vizard-load-existing" onClick={loadExisting} disabled={!settings.configured}
            className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/80 text-sm disabled:opacity-50">Load existing project</button>
          {projectId && <span className="text-white/40 text-xs self-center">Project: {projectId}</span>}
        </div>
      </div>

      {/* Clips */}
      {(projectStatus === 'processing' || polling) && (
        <div className="p-6 rounded-2xl bg-[#111] border border-white/10 text-center">
          <Loader2 size={28} className="mx-auto animate-spin text-emerald-400 mb-2" />
          <p className="text-white/70 text-sm">Vizard is cutting your video… this takes 2–5 minutes. You can leave this tab open.</p>
        </div>
      )}

      {clips.length > 0 && (
        <div className="p-5 rounded-2xl bg-[#111] border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Clips ({clips.length})</h3>
            <div className="flex gap-2">
              <button onClick={() => { const all = {}; clips.forEach(c => { all[c.videoId] = true; }); setSelectedClips(all); }}
                className="px-3 py-1.5 rounded-lg bg-white/5 text-white/70 text-xs hover:bg-white/10">Select all</button>
              <button onClick={() => setSelectedClips({})} className="px-3 py-1.5 rounded-lg bg-white/5 text-white/70 text-xs hover:bg-white/10">Clear</button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {clips.map(c => (
              <div data-testid={`vizard-clip-${c.videoId}`} key={c.videoId}
                className={`rounded-xl border overflow-hidden transition-all ${selectedClips[c.videoId] ? 'border-emerald-500 bg-emerald-500/5' : 'border-white/10 bg-white/[0.02]'}`}>
                <div className="relative aspect-[9/16] bg-black">
                  <video src={c.videoUrl} controls className="w-full h-full object-cover" preload="metadata" />
                  {c.viralScore && (
                    <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-black/70 text-xs text-emerald-300">Viral {c.viralScore}/10</div>
                  )}
                </div>
                <div className="p-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input data-testid={`vizard-select-clip-${c.videoId}`} type="checkbox" checked={!!selectedClips[c.videoId]} onChange={() => toggleClip(c.videoId)}
                      className="w-4 h-4 accent-emerald-500" />
                    <span className="text-white text-sm font-medium line-clamp-1">{c.title || `Clip ${c.videoId}`}</span>
                  </label>
                  <textarea data-testid={`vizard-caption-${c.videoId}`} rows={2} value={captions[c.videoId] || ''}
                    onChange={e => setCaptions(prev => ({ ...prev, [c.videoId]: e.target.value }))}
                    placeholder="Caption for this clip"
                    className="w-full p-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs resize-none" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule panel */}
      {clips.length > 0 && (
        <div className="p-5 rounded-2xl bg-[#111] border border-white/10 space-y-4">
          <h3 className="text-white font-semibold">Schedule {chosenCount} selected clip{chosenCount === 1 ? '' : 's'}</h3>

          <div>
            <p className="text-white/60 text-xs mb-2">Distribution</p>
            <div className="flex gap-2">
              <button data-testid="vizard-mode-slots" onClick={() => setSlotMode('slots')}
                className={`px-3 py-1.5 rounded-lg text-xs ${slotMode === 'slots' ? 'bg-emerald-500 text-white' : 'bg-white/5 text-white/70'}`}>Daily time slots</button>
              <button data-testid="vizard-mode-interval" onClick={() => setSlotMode('interval')}
                className={`px-3 py-1.5 rounded-lg text-xs ${slotMode === 'interval' ? 'bg-emerald-500 text-white' : 'bg-white/5 text-white/70'}`}>Every N hours</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Start date</label>
              <input data-testid="vizard-start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full p-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm" />
            </div>
            {slotMode === 'interval' ? (
              <div>
                <label className="block text-white/60 text-xs mb-1.5">Hours between posts</label>
                <input data-testid="vizard-interval-hours" type="number" min="0.25" step="0.25" value={intervalHours} onChange={e => setIntervalHours(e.target.value)}
                  className="w-full p-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm" />
              </div>
            ) : (
              <div>
                <label className="block text-white/60 text-xs mb-1.5">Time slots (local)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {timeSlots.map(s => (
                    <span key={s} className="px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-1">
                      {s}
                      <button onClick={() => setTimeSlots(prev => prev.filter(x => x !== s))} className="text-white/60 hover:text-red-400"><X size={12} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input data-testid="vizard-new-slot" type="time" value={newSlot} onChange={e => setNewSlot(e.target.value)}
                    className="flex-1 p-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
                  <button data-testid="vizard-add-slot" onClick={addSlot} className="px-3 py-2 rounded-lg bg-white/5 text-white/80 text-xs hover:bg-white/10">Add slot</button>
                </div>
              </div>
            )}
          </div>

          {slotMode === 'interval' && (
            <div>
              <label className="block text-white/60 text-xs mb-1.5">Starting time</label>
              <div className="flex gap-2">
                <input type="time" value={timeSlots[0] || '09:00'} onChange={e => setTimeSlots([e.target.value, ...timeSlots.slice(1)])}
                  className="p-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
              </div>
            </div>
          )}

          {/* Accounts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-white/60 text-xs">Publish to accounts</p>
              <div className="flex gap-2">
                <button onClick={() => setSelectedAccounts(accounts.map(a => a.id))} className="text-emerald-400 text-xs">Select all</button>
                <button onClick={() => setSelectedAccounts([])} className="text-white/40 text-xs">Clear</button>
              </div>
            </div>
            {accounts.length === 0 ? (
              <p className="text-white/40 text-xs">No connected accounts. Add one in the Connections tab.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {accounts.map(a => (
                  <label key={a.id} data-testid={`vizard-account-${a.id}`}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm ${selectedAccounts.includes(a.id) ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                    <input type="checkbox" checked={selectedAccounts.includes(a.id)} onChange={() => toggleAccount(a.id)} className="w-4 h-4 accent-emerald-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs truncate">{a.username || a.display_name || 'Account'}</p>
                      <p className="text-white/40 text-[10px] capitalize">{a.platform}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button data-testid="vizard-bulk-submit" disabled={scheduling || chosenCount === 0 || selectedAccounts.length === 0} onClick={schedule}
            className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {scheduling ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />}
            Schedule {chosenCount} clip{chosenCount === 1 ? '' : 's'} to {selectedAccounts.length} account{selectedAccounts.length === 1 ? '' : 's'}
          </button>
        </div>
      )}
    </div>
  );
}
